import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Archive,
  List,
  Bot,
  CalendarDays,
  CheckCircle2,
  CornerDownLeft,
  HelpCircle,
  Inbox,
  LayoutDashboard,
  Map,
  NotebookPen,
  Plus,
  Search,
  Settings,
  Sun,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useDetail } from '@/lib/hooks';
import { openNewNode } from '@/lib/ui-events';
import { cn } from '@/lib/utils';
import { TypeIcon } from './node-meta';
import { Kbd } from './ui';

interface Item {
  id: string;
  label: string;
  hint?: string;
  icon: ReactIcon;
  run: () => void;
}
type ReactIcon = LucideIcon | (() => React.ReactNode);

const PAGES: { label: string; to: string; icon: LucideIcon }[] = [
  { label: 'Today', to: '/', icon: Sun },
  { label: 'Inbox', to: '/inbox', icon: Inbox },
  { label: 'Lists', to: '/lists', icon: List },
  { label: 'Next actions', to: '/next', icon: Zap },
  { label: 'Calendar', to: '/calendar', icon: CalendarDays },
  { label: 'Done', to: '/done', icon: CheckCircle2 },
  { label: 'Daily prep', to: '/review/daily', icon: NotebookPen },
  { label: 'Weekly review', to: '/review/weekly', icon: NotebookPen },
  { label: 'Overview', to: '/overview', icon: LayoutDashboard },
  { label: 'Agent tasks', to: '/agents', icon: Bot },
  { label: 'Questions', to: '/questions', icon: HelpCircle },
  { label: 'Map', to: '/map', icon: Map },
  { label: 'Archive', to: '/archive', icon: Archive },
  { label: 'Settings', to: '/settings', icon: Settings },
];

/** Cmd/Ctrl+K: jump to a page or node, or create a task from the typed text. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { open: openDetail } = useDetail();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('gb:palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('gb:palette', onOpen);
    };
  }, []);
  useEffect(() => {
    if (!open) {
      setQ('');
      setActive(0);
    }
  }, [open]);

  const term = q.trim();
  const { data: found } = useQuery({
    queryKey: ['palette', term],
    queryFn: () => api.nodes({ q: term, include_done: true, limit: 8 }),
    enabled: open && term.length > 0,
  });
  const create = useMutation({
    mutationFn: (type: 'task' | 'idea') => api.createNode({ type, title: term }),
    onSuccess: (n) => toast.success(`Captured to inbox: ${n.title}`),
  });

  const items = useMemo<Item[]>(() => {
    const close = () => setOpen(false);
    const lower = term.toLowerCase();
    const pages = PAGES.filter((p) => !lower || p.label.toLowerCase().includes(lower)).map<Item>((p) => ({
      id: `p:${p.to}`,
      label: p.label,
      hint: 'Go to',
      icon: p.icon,
      run: () => {
        close();
        navigate(p.to);
      },
    }));
    const nodes = (found ?? []).map<Item>((n) => ({
      id: `n:${n.id}`,
      label: n.title,
      hint: n.type,
      icon: () => <TypeIcon type={n.type} size={16} />,
      run: () => {
        close();
        openDetail(n.id);
      },
    }));
    const make = (label: string, type: 'list' | 'goal' | 'area'): Item => ({
      id: `new:${type}`,
      label,
      hint: 'Create',
      icon: Plus,
      run: () => {
        close();
        openNewNode(type);
      },
    });
    const newItems = [make('New list…', 'list'), make('New goal…', 'goal'), make('New area…', 'area')].filter(
      (i) => !lower || i.label.toLowerCase().includes(lower) || 'create new'.includes(lower),
    );
    const creates: Item[] = term
      ? [
          {
            id: 'c:task',
            label: `Create task "${term}"`,
            hint: 'Inbox',
            icon: Plus,
            run: () => (create.mutate('task'), close()),
          },
          {
            id: 'c:idea',
            label: `Create idea "${term}"`,
            hint: 'Inbox',
            icon: Plus,
            run: () => (create.mutate('idea'), close()),
          },
        ]
      : [];
    return term ? [...nodes, ...newItems, ...pages, ...creates] : [...pages, ...newItems];
  }, [term, found, navigate, openDetail, create]);

  const run = (i: number) => items[Math.min(i, items.length - 1)]?.run();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-black/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[12%] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 animate-pop overflow-hidden rounded-2xl border border-border bg-surface shadow-[var(--shadow-pop)]"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <div className="flex items-center gap-2 border-b border-border px-4">
            <Search size={16} className="text-muted-fg" />
            <input
              autoFocus
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((a) => Math.min(a + 1, items.length - 1));
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((a) => Math.max(a - 1, 0));
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  run(active);
                }
              }}
              placeholder="Search nodes, jump to a page, or type to create…"
              className="h-12 flex-1 bg-transparent text-base outline-none placeholder:text-muted-fg/70"
            />
            <Kbd>esc</Kbd>
          </div>
          <ul className="max-h-[50vh] overflow-y-auto p-1.5" role="listbox">
            {items.map((it, i) => {
              const Icon = it.icon as LucideIcon;
              return (
                <li key={it.id} role="option" aria-selected={i === active}>
                  <button
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm',
                      i === active && 'bg-accent',
                    )}
                    onMouseMove={() => setActive(i)}
                    onClick={() => it.run()}
                  >
                    <span className="flex w-5 justify-center text-muted-fg">
                      <Icon size={16} />
                    </span>
                    <span className="flex-1 truncate">{it.label}</span>
                    {it.hint && <span className="text-xs text-muted-fg">{it.hint}</span>}
                    {i === active && <CornerDownLeft size={13} className="text-muted-fg" />}
                  </button>
                </li>
              );
            })}
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-sm text-muted-fg">No results</li>
            )}
          </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
